import { useState, useEffect } from "react";
import { ChevronDown, Building2 } from "lucide-react";
import { clsx } from "clsx";
import { useAuth } from "@/auth/useAuth";
import { getTenants } from "@/api/endpoints/tenants";
import {
  getActiveTenantId,
  registerTenants,
  setActiveTenant,
} from "@/api/tenantContext";
import type { Tenant } from "@/types";

// Selection itself lives in api/tenantContext so the API client can read
// it and scope requests to the selected tenant.
const listeners = new Set<(id: number | null) => void>();

export { getActiveTenantId };

export function setActiveTenantId(id: number | null): void {
  setActiveTenant(id);
  listeners.forEach((fn) => fn(id));
}

export function useActiveTenant(): [
  number | null,
  (id: number | null) => void,
] {
  const [tenantId, setTenantId] = useState(getActiveTenantId());

  useEffect(() => {
    const listener = (id: number | null) => setTenantId(id);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return [tenantId, setActiveTenantId];
}

export function TenantSelector() {
  const { isAdmin, user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setTenant] = useActiveTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      try {
        const data = await getTenants();
        setTenants(data);
        // Slugs must be known before a selection can be sent to the API.
        registerTenants(data);

        // Auto-select tenant
        if (activeTenant == null && data.length > 0) {
          const userTenant = user?.tenantId
            ? data.find((t) => t.id === user.tenantId)
            : null;
          setTenant(userTenant?.id ?? data[0].id);
        }
      } catch {
        // Tenants may not be loaded yet
      } finally {
        setIsLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentTenant = tenants.find((t) => t.id === activeTenant);

  // Non-admin users with a single tenant don't need the selector
  if (!isAdmin && tenants.length <= 1) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-gray-800/50 px-3 py-2">
        <Building2 className="h-4 w-4 text-gray-500" />
        <span className="truncate text-sm text-gray-300">
          {currentTenant?.name ?? "Loading..."}
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex w-full items-center gap-2 rounded-lg border border-gray-700 bg-gray-800/50 px-3 py-2 text-left text-sm transition-colors hover:border-gray-600"
      >
        <Building2 className="h-4 w-4 flex-shrink-0 text-gray-500" />
        <span className="flex-1 truncate text-gray-300">
          {currentTenant?.name ?? "Select tenant"}
        </span>
        <ChevronDown
          className={clsx(
            "h-4 w-4 flex-shrink-0 text-gray-500 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 py-1 shadow-xl">
            {tenants.map((tenant) => (
              <button
                key={tenant.id}
                onClick={() => {
                  setTenant(tenant.id);
                  setIsOpen(false);
                }}
                className={clsx(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                  tenant.id === activeTenant
                    ? "bg-blue-600/20 text-blue-400"
                    : "text-gray-300 hover:bg-gray-700"
                )}
              >
                <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{tenant.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
